/* eslint-disable no-console */
import { effect, Injectable, isDevMode, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DebugService {
  private debugEnabled = signal(isDevMode());

  private componentStats = signal<Record<string, { renders: number; updates: Record<string, number> }>>({});

  private logColors: Record<string, string> = {};

  public constructor() {
    effect(() => {
      if (this.debugEnabled()) {
        const stats = this.componentStats();
        console.log('🔍 Debug Statistics', stats, Object.keys(stats).length > 0);
        if (Object.keys(stats).length > 0) {
          console.groupCollapsed('🔍 Debug Statistics');
          for (const [componentId, data] of Object.entries(stats)) {
            console.groupCollapsed(`Component: ${componentId} (${data.renders} renders)`);

            if (Object.keys(data.updates).length > 0) {
              console.table(
                Object.entries(data.updates).map(([property, count]) => ({
                  Property: property,
                  Updates: count,
                })),
              );
            } else {
              console.log('No property updates tracked');
            }
            console.groupEnd();
          }
          console.groupEnd();
        }
      }
    });
  }

  /**
   * Enables or disables debug mode for the application.
   *
   * When enabling debug mode, it logs a message indicating debug mode is active.
   * When disabling debug mode, it logs a message indicating debug mode is deactivated
   * and resets all statistical data.
   *
   * @param {boolean} enabled - A boolean indicating whether debug mode should be enabled (true) or disabled (false).
   * @returns {void}
   */
  public setDebugEnabled(enabled: boolean): void {
    this.debugEnabled.set(enabled);

    if (enabled) {
      console.log('%c🔍 Debug Mode Enabled', 'background:#2196F3; color:white; padding:2px 5px; border-radius:3px;');
    } else {
      console.log('%c🔍 Debug Mode Disabled', 'background:#9E9E9E; color:white; padding:2px 5px; border-radius:3px;');
      this.resetStats(); // Clear all stats when disabling
    }
  }

  /**
   * Determines whether debug mode is enabled.
   *
   * @returns {boolean} true if debug mode is enabled, false otherwise.
   */
  public isDebugEnabled(): boolean {
    return this.debugEnabled();
  }

  /**
   * Logs the render process of a specific component and updates its render statistics.
   *
   * @param {string} componentId - The unique identifier of the component being rendered.
   * @returns {void} This method does not return a value.
   */
  public logRender(componentId: string): void {
    if (!this.debugEnabled()) return;

    this.componentStats.update((stats) => {
      const componentStat = stats[componentId] || { renders: 0, updates: {} };
      return {
        ...stats,
        [componentId]: {
          ...componentStat,
          renders: componentStat.renders + 1,
        },
      };
    });

    const color = this.getComponentColor(componentId);

    console.log(
      `%c[${componentId}]%c rendered (${this.componentStats()[componentId].renders} times)`,
      `background:${color}; color:white; padding:2px 5px; border-radius:3px;`,
      'color:inherit;',
    );
  }

  /**
   * Tracks the update of a specific property within a component, logs the update, and increments the count of updates for that property.
   * This method is beneficial for debugging purposes.
   *
   * @param {string} componentId - The unique identifier for the component being updated.
   * @param {string} propertyName - The name of the property within the component that is being updated.
   * @param {unknown} value - The new value assigned to the updated property.
   * @returns {void} Does not return a value.
   */
  public trackUpdate(componentId: string, propertyName: string, value: unknown): void {
    if (!this.debugEnabled()) return;

    this.componentStats.update((stats) => {
      const componentStat = stats[componentId] || { renders: 0, updates: {} };
      const updateCount = (componentStat.updates[propertyName] || 0) + 1;

      return {
        ...stats,
        [componentId]: {
          ...componentStat,
          updates: {
            ...componentStat.updates,
            [propertyName]: updateCount,
          },
        },
      };
    });

    console.log(`[Debug] "${componentId}.${propertyName}" updated to:`, value);

    const color = this.getComponentColor(componentId);
    const propertyColor = this.getPropertyColor(propertyName);

    console.log(
      `%c[${componentId}]%c %c${propertyName}%c updated (${this.componentStats()[componentId].updates[propertyName]} times):`,
      `background:${color}; color:white; padding:2px 5px; border-radius:3px 0 0 3px;`,
      'color:inherit;',
      `background:${propertyColor}; color:white; padding:2px 5px; border-radius:0 3px 3px 0;`,
      'color:inherit;',
      value,
    );
  }

  /**
   * Retrieves statistical information about component activity.
   *
   * @returns {Record<string, { renders: number; updates: Record<string, number> }>}
   * An object where each key represents a component identifier,
   * and the value is an object containing the number of renders
   * and a record of updates keyed by property names with corresponding update counts.
   */
  public getStats(): Record<string, { renders: number; updates: Record<string, number> }> {
    return this.componentStats();
  }

  /**
   * Resets statistical data for a specific component or all components.
   *
   * @param {string} [componentId] - Optional ID of the component whose stats should be reset. If not provided, all stats will be reset.
   * @returns {void} This method does not return a value.
   */
  public resetStats(componentId?: string): void {
    if (componentId) {
      this.componentStats.update((stats) => {
        const { [componentId]: _, ...rest } = stats;
        return rest;
      });
      console.log(
        `%c[Debug]%c Stats reset for ${componentId}`,
        'background:#FF5722; color:white; padding:2px 5px; border-radius:3px;',
        'color:inherit;',
      );
    } else {
      this.componentStats.set({});
      console.log(
        `%c[Debug]%c All stats reset`,
        'background:#FF5722; color:white; padding:2px 5px; border-radius:3px;',
        'color:inherit;',
      );
    }
  }

  /**
   * Logs render performance details for a specific component if debugging is enabled.
   *
   * @param {string} componentId - The identifier for the component being monitored.
   * @param {number} duration - The duration it took for the component to render, in milliseconds.
   * @param {number} threshold - The performance threshold in milliseconds used to determine the render status.
   * @returns {void} This method does not return a value.
   */
  public captureRenderPerformance(componentId: string, duration: number, threshold: number): void {
    if (!this.debugEnabled()) return;

    const color =
      duration > threshold
        ? '#F44336' // Red for slow renders
        : duration > threshold * 0.7
          ? '#FF9800' // Orange for borderline renders
          : '#4CAF50'; // Green for fast renders

    console.log(
      `%c[Performance]%c ${componentId} render took %c${duration.toFixed(2)}ms%c (threshold: ${threshold}ms)`,
      'background:#673AB7; color:white; padding:2px 5px; border-radius:3px;',
      'color:inherit;',
      `background:${color}; color:white; padding:2px 5px; border-radius:3px;`,
      'color:inherit;',
    );
  }

  /**
   * Captures and logs a snapshot of the data associated with a specific component.
   * This method outputs the data in a grouped and styled format in the console,
   * provided that debugging mode is enabled.
   *
   * @param {string} componentId - The unique identifier of the component for which the snapshot is taken.
   * @param {Record<string, any>} data - An object containing the data to be logged in the snapshot.
   * @returns {void} This method does not return any value.
   */
  public takeSnapshot(componentId: string, data: Record<string, any>): void {
    if (!this.debugEnabled()) return;

    const timestamp = new Date().toISOString();
    const color = this.getComponentColor(componentId);

    console.groupCollapsed(
      `%c[${componentId}]%c Snapshot at ${timestamp.slice(11, 23)}`,
      `background:${color}; color:white; padding:2px 5px; border-radius:3px;`,
      'color:inherit;',
    );

    for (const [key, value] of Object.entries(data)) {
      console.log(`%c${key}:%c`, 'font-weight:bold;', 'font-weight:normal;', value);
    }

    console.groupEnd();
  }

  /**
   * Retrieves the color associated with a specific component. If no color exists for the provided componentId,
   * a new color is generated and stored for future use.
   *
   * @param {string} componentId - The unique identifier of the component for which the color is being retrieved or generated.
   * @returns {string} The color assigned to the specified component in HSL format.
   */
  private getComponentColor(componentId: string): string {
    if (!this.logColors[componentId]) {
      // Generate a color based on the componentId
      const hue = this.hashString(componentId) % 360;
      this.logColors[componentId] = `hsl(${hue}, 70%, 45%)`;
    }

    return this.logColors[componentId];
  }

  /**
   * Retrieves or generates a color associated with a given property name.
   * If a color does not already exist for the given property, it generates
   * a new color based on a hash of the property name and stores it.
   *
   * @param {string} propertyName - The name of the property for which the color is retrieved or generated.
   * @returns {string} The color associated with the given property in HSL format.
   */
  private getPropertyColor(propertyName: string): string {
    if (!this.logColors[`prop_${propertyName}`]) {
      // Generate a color based on the property name
      const hue = (this.hashString(propertyName) % 300) + 30;
      this.logColors[`prop_${propertyName}`] = `hsl(${hue}, 60%, 40%)`;
    }

    return this.logColors[`prop_${propertyName}`];
  }

  /**
   * Generates a hash code as a positive 32-bit integer for the given string.
   *
   * @param {string} string_ - The input string to be hashed.
   * @returns {number} A positive 32-bit integer representing the hash of the input string.
   */
  private hashString(string_: string): number {
    let hash = 0;
    for (let index = 0; index < string_.length; index++) {
      hash = (hash << 5) - hash + string_.charCodeAt(index);
      hash = Math.trunc(hash); // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Logs an error message to the console with a formatted output if debug mode is enabled.
   *
   * @param {string} componentId - The identifier of the component where the error occurred.
   * @param {string} message - The error message to be logged.
   * @param {unknown} error - The error object or additional information associated with the error.
   * @returns {void}
   */
  public logError(componentId: string, message: string, error: unknown): void {
    if (!this.debugEnabled()) return;

    console.error(
      `%c[${componentId}]%c ERROR: ${message}`,
      `background:#F44336; color:white; padding:2px 5px; border-radius:3px;`,
      'color:#F44336; font-weight:bold;',
      error,
    );
  }

  /**
   * Logs a warning message to the console with a specific component identifier and optional data.
   *
   * @param {string} componentId - The identifier of the component where the warning is originating.
   * @param {string} message - The warning message to be logged.
   * @param {unknown} [data] - Optional additional data to be logged with the warning message.
   * @returns {void} This method does not return any value.
   */
  public logWarning(componentId: string, message: string, data?: unknown): void {
    if (!this.debugEnabled()) return;

    if (data) {
      console.warn(
        `%c[${componentId}]%c WARNING: ${message}`,
        `background:#FF9800; color:white; padding:2px 5px; border-radius:3px;`,
        'color:#FF9800; font-weight:bold;',
        data,
      );
    } else {
      console.warn(
        `%c[${componentId}]%c WARNING: ${message}`,
        `background:#FF9800; color:white; padding:2px 5px; border-radius:3px;`,
        'color:#FF9800; font-weight:bold;',
      );
    }
  }
}
